<template>
    <div class="step-preview">
        <ConfirmModal />
        <h3>4. Aperçu</h3>

        <div v-if="loading" class="loading-block">
            <span class="spinner" aria-hidden="true"></span>
            <span>Analyse en cours{{ rowCountHint }}...</span>
        </div>

        <template v-else>
            <p v-if="issueCount" class="issue-banner">
                ⚠️ {{ issueCount }} ligne{{ issueCount > 1 ? 's' : '' }} en erreur -- corrigez avant de valider.
            </p>
            <p v-else class="ok-banner">✅ Aucune erreur -- prêt à valider.</p>

            <p v-if="unregisteredCount" class="unregistered-banner">
                {{ unregisteredCount }} joueur{{ unregisteredCount > 1 ? 's' : '' }} non inscrit{{ unregisteredCount > 1 ? 's' : '' }}
                à ce tournoi -- si ce sont bien de vrais participants (tournoi sans inscriptions
                natives), inscrivez-les tous en un clic :
                <button type="button" class="btn-secondary" :disabled="registering" @click="onQuickRegister">
                    {{ registering ? 'Inscription...' : `Ajouter les ${unregisteredCount} inscription(s) manquante(s)` }}
                </button>
            </p>

            <div class="rows-table-wrapper">
                <table class="rows-table">
                    <thead>
                        <tr>
                            <th>Catégorie</th>
                            <th>Nom brut</th>
                            <th>Nom enrichi</th>
                            <th>Club</th>
                            <th>Licencié</th>
                            <th>Code CLT</th>
                            <th>Points</th>
                            <th>Issue</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(row, idx) in enrichedRows" :class="{ 'row-issue': row.issue }" :key="idx">
                            <td>{{ categoryLabel(row.category) }}</td>
                            <td><input v-model="editableRows[idx].player_name_raw" class="form-input-sm name-input" /></td>
                            <td class="wrap-cell">{{ row.display_name }}</td>
                            <td>{{ row.club_code ?? '—' }}</td>
                            <td>{{ row.is_licensed ? 'Oui' : 'Non' }}</td>
                            <td><input v-model="editableRows[idx].code_clt" class="form-input-sm code-clt-input" /></td>
                            <td>
                                {{ row.points }}
                                <span v-if="row.no_match_won" class="hint" title="Règlement FFD : aucun match gagné">
                                    (aucun match gagné)
                                </span>
                            </td>
                            <td class="issue-cell">{{ row.issue ?? '' }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="form-actions">
                <button type="button" class="btn-secondary" @click="$emit('back')">← Retour</button>
                <button type="button" class="btn-secondary" :disabled="loading" @click="runPreview">
                    Ré-analyser
                </button>
                <button
                    type="button"
                    class="btn-submit"
                    :disabled="!!issueCount || committing"
                    @click="onCommit"
                >
                    {{ committing ? 'Validation...' : 'Valider l\'import' }}
                </button>
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { toast } from 'vue3-toastify'
import dartsHubApi from '@/axios/dartsHubApi'
import { handleError } from '@/functions/utils'
import ConfirmModal from '@/components/ConfirmModal.vue'
import { useConfirm } from '@/composables/useConfirm'
import { RANKING_CATEGORIES, type ImportRow, type EnrichedRow, type WizardParams } from './types'

const props = defineProps<{ params: WizardParams; rows: ImportRow[] }>()
const emit = defineEmits<{ next: [result: { ranking_tournament_id: number; rows_imported: number }]; back: [] }>()

const { confirm } = useConfirm()

// L'aperçu et la validation rapprochent chaque ligne de toutes les licences : plusieurs
// secondes pour un tournoi complet (~200 lignes, tous tableaux), au-delà des 30 s par
// défaut de dartsHubApi sur un serveur chargé (vu le 2026-10-06 : "Network error").
const IMPORT_TIMEOUT_MS = 120000

const loading = ref(false)
const committing = ref(false)
const registering = ref(false)
const enrichedRows = ref<EnrichedRow[]>([])
// Copie éditable, indexée comme enrichedRows -- réutilisée pour reconstruire les rows du
// payload lors du "Ré-analyser"/commit (jamais props.rows directement, pour ne pas muter
// l'état de l'étape précédente pendant qu'on corrige ici).
const editableRows = ref<{ player_name_raw: string; code_clt: string }[]>([])

// Un import de plusieurs centaines de lignes (ex. classement fédéral multi-catégories)
// peut prendre plusieurs secondes côté serveur (rapprochement flou avec les licences/
// inscriptions) -- rappeler le nombre de lignes explique pourquoi l'attente est plus
// longue que d'habitude, plutôt que de laisser croire à un blocage.
const rowCountHint = computed(() => (props.rows.length ? ` (${props.rows.length} lignes)` : ''))

const issueCount = computed(() => enrichedRows.value.filter((r) => r.issue).length)
// cf. find_unregistered_players (backend) : message exact "joueur non inscrit à ce
// tournoi : ...". On ne détecte QUE cette famille d'issue -- les autres (barème introuvable,
// code_clt manquant...) ne se résolvent pas en ajoutant une inscription.
const unregisteredCount = computed(
    () => enrichedRows.value.filter((r) => r.issue?.includes('non inscrit')).length
)

function categoryLabel(value: string): string {
    return RANKING_CATEGORIES.find((c) => c.value === value)?.label ?? value
}

function buildPayloadRows() {
    return props.rows.map((r, idx) => ({
        category: r.category,
        player_name_raw: editableRows.value[idx]?.player_name_raw ?? r.player_name_raw,
        code_clt: editableRows.value[idx]?.code_clt ?? r.code_clt,
        no_match_won: r.no_match_won ?? false,
    }))
}

async function runPreview() {
    loading.value = true
    try {
        const { data } = await dartsHubApi.post('/leaderboard/import/preview', {
            source_type_code: props.params.sourceTypeCode,
            season_id: props.params.seasonId,
            scope_level: props.params.scopeLevel,
            scope_id: props.params.scopeId,
            event_id: props.params.eventId,
            tournament_date: props.params.tournamentDate,
            rows: buildPayloadRows(),
        }, { timeout: IMPORT_TIMEOUT_MS })
        enrichedRows.value = data.rows
        editableRows.value = data.rows.map((r: EnrichedRow) => ({
            player_name_raw: r.player_name_raw,
            code_clt: r.code_clt ?? '',
        }))
    } catch (err) {
        handleError(err, 'aperçu de l\'import')
    } finally {
        loading.value = false
    }
}

onMounted(() => {
    editableRows.value = props.rows.map((r) => ({ player_name_raw: r.player_name_raw, code_clt: r.code_clt }))
    runPreview()
})

async function onQuickRegister() {
    const flagged = enrichedRows.value
        .map((r, idx) => ({ row: r, idx }))
        .filter(({ row }) => row.issue?.includes('non inscrit'))
    if (!flagged.length) return

    const accepted = await confirm(
        `Ajouter ${flagged.length} inscription(s) pour "${props.params.name}" à partir des noms saisis ? ` +
        'À utiliser seulement si ce sont de vrais participants (tournoi sans inscriptions natives) -- ' +
        'pas pour corriger une faute de frappe (corrigez plutôt le nom directement dans le tableau).'
    )
    if (!accepted) return

    registering.value = true
    try {
        await dartsHubApi.post('/leaderboard/import/quick-register', {
            event_id: props.params.eventId,
            scope_level: props.params.scopeLevel,
            scope_id: props.params.scopeId,
            rows: flagged.map(({ row, idx }) => ({
                category: row.category,
                player_name_raw: editableRows.value[idx]?.player_name_raw ?? row.player_name_raw,
            })),
        })
        toast.success(`${flagged.length} inscription(s) ajoutée(s).`)
        await runPreview()
    } catch (err) {
        handleError(err, 'ajout des inscriptions manquantes')
    } finally {
        registering.value = false
    }
}

async function onCommit() {
    const accepted = await confirm(
        `Valider l'import de ${enrichedRows.value.length} ligne(s) pour "${props.params.name}" ? ` +
        'Ça alimente à la fois le classement de ce tournoi et le classement général.'
    )
    if (!accepted) return

    committing.value = true
    try {
        const { data } = await dartsHubApi.post('/leaderboard/import/commit', {
            source_type_code: props.params.sourceTypeCode,
            season_id: props.params.seasonId,
            scope_level: props.params.scopeLevel,
            scope_id: props.params.scopeId,
            event_id: props.params.eventId,
            tournament_date: props.params.tournamentDate,
            rows: buildPayloadRows(),
            name: props.params.name,
            place: props.params.place,
            source: props.params.source === 'dartup' || props.params.source === 'd4w' || props.params.source === '3kdarts'
                ? props.params.source
                : 'manual',
        }, { timeout: IMPORT_TIMEOUT_MS })
        toast.success('Import validé.')
        emit('next', data)
    } catch (err) {
        handleError(err, 'validation de l\'import')
    } finally {
        committing.value = false
    }
}
</script>

<style scoped>
.step-preview h3 {
    margin-bottom: 1rem;
}

.loading-block {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    color: var(--color-fg-muted, #888);
}

.spinner {
    width: 1.1rem;
    height: 1.1rem;
    border: 2px solid var(--color-light-shadow, #ddd);
    border-top-color: var(--color-accent, #1a3a5c);
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.issue-banner {
    color: var(--color-danger, #d9534f);
    font-weight: 600;
    margin-bottom: 1rem;
}

.ok-banner {
    color: var(--color-success, #38c793);
    font-weight: 600;
    margin-bottom: 1rem;
}

.unregistered-banner {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;
    font-size: 0.9rem;
    color: var(--color-fg-muted, #888);
    margin-bottom: 1rem;
}

.hint {
    font-size: 0.8rem;
    color: var(--color-fg-muted, #888);
}

.rows-table-wrapper {
    overflow-x: auto;
}

.rows-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.85rem;
}

.rows-table th,
.rows-table td {
    padding: 0.35rem 0.5rem;
    border-bottom: 1px solid var(--color-light-shadow, #eee);
    text-align: left;
    white-space: nowrap;
}

.row-issue {
    background: rgba(217, 83, 79, 0.08);
}

/* Sélecteur `td.` : sinon `.rows-table td { nowrap }`, plus spécifique, l'emportait et les
   colonnes longues (erreur, nom enrichi) élargissaient le tableau au-delà de la page. */
.rows-table td.issue-cell {
    color: var(--color-danger, #d9534f);
    white-space: normal;
    min-width: 180px;
}

.rows-table td.wrap-cell {
    white-space: normal;
    min-width: 200px;
}

.form-input-sm {
    padding: 0.3rem 0.4rem;
    border: 1px solid var(--color-light-shadow, #ddd);
    border-radius: 4px;
    font-size: 0.85rem;
}

.name-input {
    width: 100%;
    min-width: 240px;
}

.code-clt-input {
    max-width: 80px;
}

.form-actions {
    display: flex;
    gap: 0.75rem;
    margin-top: 1.5rem;
    flex-wrap: wrap;
}

.btn-submit,
.btn-secondary {
    display: inline-flex;
    align-items: center;
    padding: 0.6rem 1.2rem;
    border-radius: var(--radius, 8px);
    font-weight: 600;
    cursor: pointer;
    border: 1px solid var(--color-accent, #1a3a5c);
}

.btn-submit {
    background: var(--color-accent, #1a3a5c);
    color: #fff;
}

.btn-submit:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.btn-secondary {
    background: transparent;
    color: var(--color-accent, #1a3a5c);
}
</style>
